import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, animate, useDragControls } from "framer-motion";
import { Tag } from "lucide-react";

const NAV_H = 56;

// Draggable bottom sheet over the map: peek / half / full snap points.
// Drag is bound to the header only so the listing feed scrolls natively.
export const ResultsDrawer = ({ children, snapSignal, resultLabel }) => {
  const calc = () => {
    const H = Math.round(window.innerHeight * 0.82) - NAV_H;
    return { H, SNAP: { full: 0, half: Math.round(H * 0.48), peek: Math.round(H * 0.78) } };
  };
  const [{ H, SNAP }, setDims] = useState(calc);
  const y = useMotionValue(SNAP.half);
  const snapRef = useRef("half");
  const controls = useDragControls();

  const go = (s) => { snapRef.current = s; animate(y, SNAP[s], { type: "spring", stiffness: 380, damping: 40 }); };

  useEffect(() => {
    const on = () => setDims(calc());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);
  // Re-seat the sheet at its current snap whenever the viewport changes.
  useEffect(() => { animate(y, SNAP[snapRef.current], { type: "spring", stiffness: 380, damping: 40 }); }, [SNAP]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (snapSignal) go(snapSignal.snap); }, [snapSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDragEnd = (_, info) => {
    const target = y.get() + info.velocity.y * 0.12;
    const nearest = Object.keys(SNAP).reduce((a, k) => (Math.abs(SNAP[k] - target) < Math.abs(SNAP[a] - target) ? k : a), "half");
    go(nearest);
  };

  return (
    <motion.div
      className="fixed inset-x-0 z-40 rounded-t-3xl bg-white shadow-[0_-14px_44px_rgba(0,0,0,0.2)] border-t border-black/5 flex flex-col"
      style={{ y, height: H, bottom: NAV_H }}
      drag="y" dragListener={false} dragControls={controls}
      dragConstraints={{ top: 0, bottom: SNAP.peek }} dragElastic={0.04} dragMomentum={false}
      onDragEnd={onDragEnd}
      data-testid="results-drawer"
    >
      <div onPointerDown={(e) => controls.start(e)} className="touch-none cursor-grab active:cursor-grabbing select-none" data-testid="drawer-handle">
        <div className="w-12 h-1.5 bg-black/20 rounded-full mx-auto mt-3" />
        <div className="flex items-center justify-center gap-2 py-3 border-b border-black/5">
          <Tag className="w-4 h-4 text-brand" aria-hidden="true" />
          <p className="text-sm font-medium text-ink">Prices include all taxes & fees</p>
          {resultLabel && <span className="text-sm text-ink-faint">· {resultLabel}</span>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-10" data-lenis-prevent data-testid="drawer-feed">
        {children}
      </div>
    </motion.div>
  );
};

export default ResultsDrawer;
